/**
 * <TutorStudentNextSessionCard> — ce qu'il faut savoir pour la prochaine séance (organism).
 *
 * Le temps fort de la fiche, le seul cadre de l'écran (`<HighlightFrame>`) : « Focus séance », le récit
 * de l'IA, puis « L'élève a dit » (ce qu'il veut travailler, le contrôle qu'il a déclaré, le
 * résumé), puis le contrôle le plus proche ; les autres sont dans l'onglet Contrôles. Un bloc sans
 * donnée ne se rend pas, et rien du tout ne rend rien : un nouvel élève n'a pas encore de brief.
 *
 * `loading` : le brief arrive ; la carte montre sa forme.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Stack } from "../../atoms/containers/Stack"
import { DelayedSkeleton, Skeleton } from "../../atoms/Skeleton"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../../molecules/Card"
import { CardList } from "../../molecules/CardList"
import { HighlightFrame } from "../../molecules/HighlightFrame"
import { SchoolAssessmentRow } from "../../molecules/SchoolAssessmentRow"
import { joinFrench } from "../../../ports/primary/ui/formatters/textFormatter"
import { type UITutorSessionBrief } from "../../../ports/primary/ui/types/tutorEleves"

const UPCOMING_EYEBROW = "Contrôle à venir"

/** « Pour mardi 17:00 · Maths », « Pour ce live · Maths », « Prochaine séance » sans date connue. */
function nextSessionTitle(brief: UITutorSessionBrief | null, live: boolean): string {
    const head = live
        ? "Pour ce live"
        : brief?.nextSessionLabel != null
          ? `Pour ${brief.nextSessionLabel}`
          : "Prochaine séance"
    return brief?.nextSessionSubjectLabel != null ? `${head} · ${brief.nextSessionSubjectLabel}` : head
}

export type TutorStudentNextSessionCardProps = {
    brief: UITutorSessionBrief | null
    /** La fiche dans le live : « Pour ce live ». Défaut `false`. */
    live?: boolean
    loading?: boolean
    /** Toucher le contrôle : l'onglet Contrôles. */
    onOpenAssessments?: () => void
    testID?: string
}

export function TutorStudentNextSessionCard(props: TutorStudentNextSessionCardProps) {
    const { brief, live = false, loading = false, onOpenAssessments, testID } = props
    const title = nextSessionTitle(brief, live)

    if (loading) {
        return (
            <HighlightFrame testID={testID}>
                <Card p="4">
                    <DelayedSkeleton accessibilityLabel="Chargement du brief">
                        <Stack gap="3">
                            <Skeleton textSize="text-xs" width={130} />
                            <Stack gap="1">
                                <Skeleton textSize="text-lg" />
                                <Skeleton textSize="text-lg" width="70%" />
                            </Stack>
                            <Skeleton shape="block" height={72} radius="md" />
                        </Stack>
                    </DelayedSkeleton>
                </Card>
            </HighlightFrame>
        )
    }

    const focus = brief?.focus != null && brief.focus !== "" ? brief.focus : null
    const said = brief?.startingBlock ?? null
    const assessment = brief?.assessments[0] ?? null
    if (focus == null && said == null && assessment == null) return null

    return (
        <HighlightFrame testID={testID}>
            <Card p="4">
                <Stack gap="3">
                    <SectionLabel>{title}</SectionLabel>
                    {focus != null && (
                        <Text size="text-lg" weight="medium">
                            {focus}
                        </Text>
                    )}
                    {said != null && (
                        <Card elevation="none" background="surface-subtle" p="3">
                            <Stack gap="1">
                                <Text size="text-sm" weight="bold" color="text-muted">
                                    L'élève a dit
                                </Text>
                                {said.wantsToWork != null && (
                                    <Text size="text-md" weight="medium">
                                        {`Veut travailler : ${said.wantsToWork}`}
                                    </Text>
                                )}
                                {said.assessmentDeclared != null && (
                                    <Text size="text-sm" weight="medium" color="warning-strong">
                                        {said.assessmentDeclared}
                                    </Text>
                                )}
                                {said.summary != null && <Text size="text-sm">{said.summary}</Text>}
                            </Stack>
                        </Card>
                    )}
                    {assessment != null && (
                        <CardList elevation="none" style={styles.inset}>
                            <SchoolAssessmentRow
                                assessment={assessment}
                                eyebrow={
                                    assessment.chapterNames != null && assessment.chapterNames.length > 0
                                        ? joinFrench(assessment.chapterNames)
                                        : UPCOMING_EYEBROW
                                }
                                onPress={onOpenAssessments}
                            />
                        </CardList>
                    )}
                </Stack>
            </Card>
        </HighlightFrame>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** Encastrée dans la carte : un contour, pas une ombre. */
    inset: { borderWidth: 1, borderColor: theme.colors.border },
}))
